import DownloadTemplate from "@app/common/components/DownloadTemplate";
import { confirmNotification } from "@app/common/components/notifications";
import { formatTimeToAmPm } from "@app/common/utils/utils";
import routes from "@app/config/routes";
import { useGetMedicationOrder, useUpdateAuthDelivery } from "@app/services/deliveryAuth/hooks";
import { Box, Button, Flex, Image, Modal, Text, Tooltip } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import JustificationModal from "../../common/components/JustificationModal";
import { usePermissions } from "@app/common/hooks/usePermissions";
import {
  RBAC_ACTIONS,
  RBAC_LABELS,
  MODULE_PERMISSION_PREFIX
} from "@app/common/utils/permissionsConstants";

const MAP_FILE_TAGS = {
  dispensary_request_complete_delivery_photography: "Fotografía",
  dispensary_request_complete_delivery_attachment: "Soporte"
};

const DeliveryAuthDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [justificationModal, setJustificationModal] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false
  });
  const [supportPreview, setSupportPreview] = useState<{
    isOpen: boolean;
    fileUrl: string;
    fileLabel: string;
  }>({
    isOpen: false,
    fileUrl: "",
    fileLabel: ""
  });

  const { can } = usePermissions();
  const canApprove = can(RBAC_ACTIONS.APPROVE, MODULE_PERMISSION_PREFIX.COMPLETE_DELIVERY);
  const canReject = can(RBAC_ACTIONS.REJECT, MODULE_PERMISSION_PREFIX.COMPLETE_DELIVERY);

  const goBack = () => {
    navigate(routes.getDeliveryAuth);
  };

  const date = (location?.state?.dataAuth?.createdAt ?? "")?.split("T")[0]?.replaceAll("/", "-");

  const time = (location?.state?.dataAuth?.createdAt ?? "")?.split("T")[1]?.replaceAll("/", "-");
  const {
    data: pdfOrder = {
      results: ""
    }
  } = useGetMedicationOrder({
    id: location.state.data
  });

  const updateAuth = useUpdateAuthDelivery(async () => {
    const doAfter = () => {
      goBack();
    };

    await doAfter();
  });

  const openJustificationModal = () => {
    setJustificationModal({ isOpen: true });
  };

  const handleRejectAuth = (value: string) => {
    onChangeStatus(0, value);
  };

  const isImageFile = (url: string) => {
    if (!url) {
      return false;
    }
    const lowerUrl = url.toLowerCase();
    return (
      lowerUrl.startsWith("data:image/") ||
      lowerUrl.endsWith(".png") ||
      lowerUrl.endsWith(".jpg") ||
      lowerUrl.endsWith(".jpeg") ||
      lowerUrl.endsWith(".gif") ||
      lowerUrl.endsWith(".webp")
    );
  };

  const openSupportPreview = (fileUrl: string, fileLabel: string) => {
    setSupportPreview({
      isOpen: true,
      fileUrl,
      fileLabel
    });
  };

  const onChangeStatus = async (approved: 1 | 0, justification?: string) => {
    if (approved === 1 && !canApprove) {
      return;
    }
    if (approved === 0 && !canReject) {
      return;
    }
    if (approved === 1) {
      await updateAuth.mutateAsync({
        approved,
        id: location?.state?.dataAuth?.rcdId as number
      });
    } else {
      await updateAuth.mutateAsync({
        approved,
        id: location?.state?.dataAuth?.rcdId,
        rcdRejectReason: justification ?? ""
      });
    }
  };

  return (
    <DownloadTemplate
      goBackButton={() => {
        goBack();
      }}
      url={pdfOrder.results.replace(/^b'/gm, "").replace(/=*'$/gm, "") as string}
      urlType="base64"
      title="Detalle de fórmula de medicamentos"
      data-testid="deliveryauthdetail-download-template"
    >
      <Box ml={"lg"} data-testid="deliveryauthdetail-container">
        <JustificationModal
          isOpen={justificationModal.isOpen}
          handleChange={handleRejectAuth}
          onClose={() => {
            setJustificationModal({
              isOpen: false
            });
          }}
        />
        <Box className="cardSearch" data-testid="deliveryauthdetail-request-info">
          <b className="secondary-color">Fecha de solicitud: &nbsp;</b>
          <span>
            {date} {formatTimeToAmPm(time)}{" "}
          </span>
          <hr />
          <div>
            <b className="primary-color">Motivo de solicitud</b>
          </div>
          <div>
            <span>{location?.state?.dataAuth?.rcdReason} </span>
          </div>
        </Box>
        {location.state?.dataAuth?.files?.length > 0 && (
          <Box className="cardSearch mt-md" data-testid="deliveryauthdetail-supporting-documents">
            {location.state?.dataAuth?.files.map(
              (file: { fileUrl: string; tag: string }, index: number) => {
                const label = MAP_FILE_TAGS[file.tag as keyof typeof MAP_FILE_TAGS] || "Documento";
                const isImage = isImageFile(file.fileUrl);

                return (
                  <Box key={index} mb={"xs"}>
                    <Text fz="sm" c={"brand-color"} fw={"bold"}>
                      {label}:
                    </Text>
                    {isImage ? (
                      <Image src={file.fileUrl} alt={label} maw={200} />
                    ) : (
                      <Button
                        variant="light"
                        size="xs"
                        onClick={() => openSupportPreview(file.fileUrl, label)}
                        data-testid={`deliveryauthdetail-support-file-${index}-button`}
                      >
                        Ver soporte
                      </Button>
                    )}
                  </Box>
                );
              }
            )}
          </Box>
        )}

        <Modal
          opened={supportPreview.isOpen}
          onClose={() => {
            setSupportPreview({ isOpen: false, fileUrl: "", fileLabel: "" });
          }}
          title={supportPreview.fileLabel || "Soporte"}
          size="xl"
          data-testid="deliveryauthdetail-support-modal"
        >
          <Box w="100%" h={600}>
            <iframe
              src={supportPreview.fileUrl}
              title={supportPreview.fileLabel || "Soporte"}
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          </Box>

          <Flex justify={"end"} mt={"md"}>
            <Button
              onClick={() => setSupportPreview({ isOpen: false, fileUrl: "", fileLabel: "" })}
              data-testid="deliveryauthdetail-support-modal-close-button"
            >
              Cerrar
            </Button>
          </Flex>
        </Modal>

        <Box className=" flex justify-around" w={"100%"} data-testid="deliveryauthdetail-actions">
          <div>
            <Tooltip label={canApprove ? "Aprobar solicitud" : RBAC_LABELS.NO_PERMISSION} withArrow>
              <Button
                variant="outline"
                color="green"
                px={"xs"}
                w={"7.5rem"}
                disabled={!canApprove}
                className={!canApprove ? "noPermissionDisabled" : ""}
                data-testid="deliveryauthdetail-approve-button"
                onClick={() => {
                  if (!canApprove) {
                    return;
                  }
                  confirmNotification(
                    "Verifique la información del usuario",
                    "¿Está seguro?",
                    () => {
                      onChangeStatus(1);
                    }
                  );
                }}
              >
                Aprobar &nbsp; <IconCheck />
              </Button>
            </Tooltip>
          </div>
          <div>
            <Tooltip label={canReject ? "Rechazar solicitud" : RBAC_LABELS.NO_PERMISSION} withArrow>
              <Button
                variant="outline"
                color="red"
                w={"7.5rem"}
                px={"xs"}
                disabled={!canReject}
                className={!canReject ? "noPermissionDisabled" : ""}
                data-testid="deliveryauthdetail-reject-button"
                onClick={() => {
                  if (!canReject) {
                    return;
                  }
                  confirmNotification("Se rechazará la solicitud", "¿Está seguro?", () => {
                    openJustificationModal();
                  });
                }}
              >
                Rechazar &nbsp; <IconX />
              </Button>
            </Tooltip>
          </div>
        </Box>
      </Box>
    </DownloadTemplate>
  );
};

export default DeliveryAuthDetail;
